import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '@/api/users';
import type { Role, User } from '@/api/types';

/**
 * GET /users has no query params (see UsersController.GetAll) - it always
 * returns the complete, unfiltered list. There is exactly one list to cache;
 * callers filter/paginate client-side over `data.items`.
 */
export const userKeys = {
  all: ['users'] as const,
  list: () => [...userKeys.all, 'list'] as const,
  search: (term: string) => [...userKeys.all, 'search', term] as const,
};

export function useUsers() {
  return useQuery({
    queryKey: userKeys.list(),
    queryFn: () => usersApi.list(),
  });
}

/**
 * Resolves basic info (name/email) for a set of user ids one GET /users/{id}
 * at a time - there's no bulk-by-ids endpoint. Used to display who a judge/
 * participant assignment (which only carries a userId) actually is, without
 * requiring the admin-only GET /users directory.
 */
export function useUsersByIds(ids: string[]) {
  const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
  const results = useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: [...userKeys.all, 'detail', id],
      queryFn: () => usersApi.get(id),
    })),
  });

  const usersById = new Map<string, User>();
  results.forEach((r, i) => {
    if (r.data) usersById.set(uniqueIds[i], r.data);
  });

  return { usersById, isLoading: results.some((r) => r.isLoading) };
}

/** Only fires with a non-empty term - matches the backend's "empty search = no results" rule. */
export function useSearchUsers(term: string) {
  return useQuery({
    queryKey: userKeys.search(term),
    queryFn: () => usersApi.search(term),
    enabled: term.trim().length > 0,
  });
}

export function useUpdateMyProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { firstName: string; lastName: string }) => usersApi.updateMyProfile(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userKeys.list() }),
  });
}

/** Changes the caller's own login email (current password required). */
export function useChangeMyEmail() {
  return useMutation({
    mutationFn: (payload: { newEmail: string; currentPassword: string }) => usersApi.changeMyEmail(payload),
  });
}

/** Provisions a brand-new account directly, with no scoped relationship yet - SuperAdmin only. */
export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { email: string; firstName: string; lastName: string }) =>
      usersApi.create(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userKeys.all }),
  });
}

/** Grants the SuperAdmin role - the only role left to grant. SuperAdmin only. */
export function useAddUserRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role }: { id: string; role: Role }) => usersApi.addRole(id, role),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userKeys.all }),
  });
}

export function useRemoveUserRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role }: { id: string; role: Role }) => usersApi.removeRole(id, role),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userKeys.all }),
  });
}

export function useActivateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersApi.activate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userKeys.list() }),
  });
}

export function useDeactivateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersApi.deactivate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userKeys.list() }),
  });
}

/** Permanently deletes the account - irreversible, SuperAdmin only. */
export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userKeys.all }),
  });
}
